import { useState } from 'react';
import { CmmImportButtons } from '../../components/cmm-import/cmmImportButtons.tsx';
import { CmmConfig, ProtocolFinal, RecordDraft } from '../../types/sharedTypeImpl.ts';
import { countRecordValuesFilled } from '../../utils/countRecordValuesFilled.ts';
import { evalOrNormalize } from '../../utils/positionInputParse.ts';
import { trpc } from '../../utils/trpc/trpc.ts';
import { EditPartCountDialog } from './editPartCountDialog.tsx';
import { MeasurementPosition } from './measurementPosition.tsx';
import { PartSelectBar } from './partSelectBar.tsx';
import { usePartSelectHotkeys } from './usePartSelectHotkeys.ts';
import { VisualInspection } from './visualInspection.tsx';

interface Props {
    protocol: ProtocolFinal;
    record: RecordDraft;
    setRecord: (record: RecordDraft) => void;
    selectedPosNr: string | null;
    setSelectedPosNr: (posNr: string) => void;
    showSelectedPosNr: boolean;
    setShowSelectedPosNr: (showSelectedPosNr: boolean) => void;
    isViewMode: boolean;
    setIsDirty: (isDirty: boolean) => void;
    cmmConfigs: CmmConfig[] | undefined;
}

export function RecordFormParts({
    protocol,
    record,
    setRecord,
    selectedPosNr,
    setSelectedPosNr,
    showSelectedPosNr,
    setShowSelectedPosNr,
    isViewMode,
    setIsDirty,
    cmmConfigs,
}: Props) {
    const [partIndex, setPartIndex] = useState(0);
    const [isEditPartCountOpen, setIsEditPartCountOpen] = useState(false);

    const { data: cmmEquipments } = trpc.equipments.getAllCustom.useQuery({ type: 'cmm/vmm' });

    const listenerDivRef = usePartSelectHotkeys({
        numParts: record.parts.length,
        partIndex,
        setPartIndex: handleSetPartIndexHotkey,
    });

    const handlePositionValueChange = (positionNumber: string, value: string | boolean | null) => {
        const position = protocol.positions.find((it) => it.positionNumber === positionNumber);
        const recordPosIdx = record.parts[partIndex].positionValues.findIndex((it) => it.positionNumber === positionNumber);
        if (!position) throw new Error(`Position ${positionNumber} not found in protocol`);
        if (recordPosIdx === -1) throw new Error(`Position ${positionNumber} not found in record`);

        const isMeasurement = position.type === 'measurement';
        const valueFinal = isMeasurement && value === '' ? null : value;

        const newRecord = structuredClone(record);
        const newPositionValue = {
            positionNumber,
            value: valueFinal,
        };
        newRecord.parts[partIndex].positionValues[recordPosIdx] = newPositionValue;
        newRecord.numValuesFilled = countRecordValuesFilled(newRecord);

        setRecord(newRecord);
        setIsDirty(true);
    };

    const handleClearPositions = () => {
        if (!window.confirm('Clear all position values for the selected part?')) return;

        const newRecord = structuredClone(record);
        newRecord.parts[partIndex].positionValues = newRecord.parts[partIndex].positionValues.map(
            ({ positionNumber }) => ({
                positionNumber,
                value: null,
            }),
        );
        newRecord.numValuesFilled = countRecordValuesFilled(newRecord);

        setRecord(newRecord);
        setIsDirty(true);
    };

    /** Returns how many parts beyond `count` (0-based) have at least one filled measurement. */
    const getFilledBeyondCount = (count: number): number => {
        return record.parts.slice(count).filter((part) => part.positionValues.some((pv) => pv.value != null && pv.value !== '')).length;
    };

    const handleEditPartCount = (newCount: number) => {
        const newRecord = structuredClone(record);

        if (newCount > record.parts.length) {
            while (newRecord.parts.length < newCount) {
                newRecord.parts.push({
                    positionValues: protocol.positions.map((it) => ({
                        positionNumber: it.positionNumber,
                        value: null,
                    })),
                });
            }
        } else if (newCount < record.parts.length) {
            newRecord.parts.splice(newCount);
        }

        newRecord.numValuesFilled = countRecordValuesFilled(newRecord);
        setRecord(newRecord);
        setIsDirty(true);

        if (partIndex >= newCount) {
            setPartIndex(newCount - 1);
        }
        setIsEditPartCountOpen(false);
    };

    // Eval and normalize the position value before selecting the next part
    function handleSetPartIndexHotkey(newPartIndex: number) {
        if (selectedPosNr) {
            const selectedPosition = protocol.positions.find((it) => it.positionNumber === selectedPosNr);
            const recordPos = record.parts[partIndex].positionValues.find((it) => it.positionNumber === selectedPosNr);
            if (selectedPosition?.type === 'measurement' && recordPos) {
                const currentValue = (recordPos.value as string | null) ?? '';
                const newValue = evalOrNormalize(currentValue);
                handlePositionValueChange(selectedPosNr, newValue);
            }
        }
        setPartIndex(newPartIndex);
    }

    const showPartSelection = record.measurementType === 'ioqc' || record.parts.length > 1;
    const partPositions = record.parts[partIndex].positionValues.map((recordPosition) => ({
        position: protocol.positions.find((it) => it.positionNumber === recordPosition.positionNumber)!,
        recordPos: recordPosition,
    }));
    return (
        <div ref={listenerDivRef}>
            <div style={{ border: '1px solid #ccc', borderBottom: 'none', marginTop: '12px', padding: '4px 2px 2px 2px' }}>
                {showPartSelection && (
                    <PartSelectBar
                        record={record}
                        partIndex={partIndex}
                        setPartIndex={setPartIndex}
                        isViewMode={isViewMode}
                        onEditPartCountClicked={() => setIsEditPartCountOpen(true)}
                    />
                )}

                {!isViewMode && (
                    <div className='button-bar' style={{ marginTop: '4px' }}>
                        <CmmImportButtons
                            protocol={protocol}
                            record={record}
                            partIndex={partIndex}
                            cmmConfigs={cmmConfigs}
                            setRecord={setRecord}
                            setIsDirty={setIsDirty}
                        />
                        <button
                            type='button'
                            style={{ marginLeft: 'auto' }}
                            onClick={handleClearPositions}
                            children='Clear positions'
                        />
                    </div>
                )}
            </div>

            {isEditPartCountOpen && (
                <EditPartCountDialog
                    isOpen={true}
                    currentCount={record.parts.length}
                    filledBeyondCount={getFilledBeyondCount}
                    onCancel={() => setIsEditPartCountOpen(false)}
                    onSubmit={handleEditPartCount}
                />
            )}

            <table className='measurement-form__positions'>
                <thead>
                    <tr>
                        <th style={{ width: '65px' }} />
                        <th>Actual</th>
                        <th>Nominal</th>
                        <th>Upper</th>
                        <th>Lower</th>
                        <th title='Deviation from middle of tolerance range'>Deviation</th>
                        <th>Tool</th>
                    </tr>
                </thead>
                <tbody>
                    {partPositions.map(({ position, recordPos }) =>
                        (() => {
                            switch (position.type) {
                                case 'measurement':
                                    return (
                                        <MeasurementPosition
                                            key={position.positionNumber}
                                            position={position}
                                            recordPos={recordPos}
                                            onUpdatePositionValue={handlePositionValueChange}
                                            isActive={position.positionNumber === selectedPosNr}
                                            isViewMode={isViewMode}
                                            cmmEquipments={cmmEquipments}
                                            onPositionFocus={setSelectedPosNr}
                                        />
                                    );

                                case 'visualInspection':
                                    return (
                                        <VisualInspection
                                            key={position.positionNumber}
                                            position={position}
                                            positionVal={recordPos.value as boolean | null}
                                            onUpdatePositionValue={handlePositionValueChange}
                                            isActive={position.positionNumber === selectedPosNr}
                                            isViewMode={isViewMode}
                                            onPositionFocus={setSelectedPosNr}
                                        />
                                    );

                                case 'generalInspection':
                                    return (
                                        <VisualInspection
                                            key={position.positionNumber}
                                            position={position}
                                            positionVal={recordPos.value as boolean | null}
                                            onUpdatePositionValue={handlePositionValueChange}
                                            isActive={position.positionNumber === selectedPosNr}
                                            isViewMode={isViewMode}
                                            onPositionFocus={setSelectedPosNr}
                                        />
                                    );
                                default:
                                    throw new Error(`Unknown position type: ${position.type}`);
                            }
                        })(),
                    )}
                </tbody>
            </table>

            <label className='with-checkbox' style={{ marginTop: '8px', marginLeft: '4px' }}>
                Show selected position number
                <input type='checkbox' checked={showSelectedPosNr} onChange={(e) => setShowSelectedPosNr(e.target.checked)} />
            </label>
        </div>
    );
}
